import classNames from 'classnames';
// d3-transition adds apis to d3-selection
import 'd3-transition';
import { drag as d3Drag, DragBehavior } from 'd3-drag';
import { event as d3Event, select } from 'd3-selection';
import {
  geoContains,
  geoIdentity,
  geoPath,
  geoTransform,
  GeoStream,
  GeoTransformPrototype,
  GeoIdentityTransform,
  GeoPath,
  GeoPermissibleObjects,
} from 'd3-geo';
import { interpolate } from 'd3-interpolate';
import { max, min } from 'd3-array';
import { scaleLinear, ScaleLinear } from 'd3-scale';
import { geoMiller, geoProject } from 'd3-geo-projection';
import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';
import {
  IconMinusAlt,
  IconPlusAlt,
  OverlayTrigger,
  Tooltip,
  Type,
} from '@spotify-internal/encore-creator-web';
import { gray20, gray75, gray95, pink, salmon } from '@spotify-internal/encore-foundation';
import { feature, mesh } from 'topojson-client';
import { topology } from 'topojson-server';
import { presimplify } from 'topojson-simplify';
import hatch6 from './6px_invert.png';
import styles from './MapChart.module.scss';
import { MapChartLegend } from './MapChartLegend';
import { getCountries50m } from './MapLoader';
import { excludeTopNPercent } from './excludeTopNPercent';

const isTouchDevice = () => 'ontouchstart' in window || !!window.navigator.maxTouchPoints;

const animationDuration = 250;
const ORIGINAL_WIDTH = 960;
const ORIGINAL_HEIGHT = 500;
const ZOOM_FACTOR = 1.6;
const HEIGHT_TRANSLATE_FACTOR = 1.8;
const SCALE_EXTENT = [1, 12];
const PERCENT = 0.01;
const BLOCK_NAME = 'map_chart';

export type MapChartData = {
  [key: string]: {
    count: number;
    rank?: number;
    name?: string;
    formatted_count?: string;
  };
};

type Props = {
  data: MapChartData;
  width: number;
  height: number;
  legendHeight: number;
  zoomDuration: number;
  highlightCountry?: string;
  onZoomButtonTransition(promise: Promise<void>): any;
  renderMapCb(data: MapChartData): any;
  onMapLoad(countries: any): any;
  setHighlightCountry(country?: string): any;
  stat: string;
};

type State = {
  k: number;
  isZooming: boolean;
  tooltipPos?: { x: number; y: number };
};

export class MapChart extends Component<Props> {
  static defaultProps = {
    width: ORIGINAL_WIDTH,
    height: ORIGINAL_HEIGHT,
    zoomDuration: animationDuration, // set to 0 in unit test
    onZoomButtonTransition() {},
    renderMapCb() {},
    onMapLoad() {},
    setHighlightCountry() {},
  };

  canvas = React.createRef<HTMLCanvasElement>();
  canvasContext?: CanvasRenderingContext2D | null;

  isTouch: boolean;
  simplify: { stream(s: GeoStream): GeoTransformPrototype & GeoStream };
  mapTransform: GeoIdentityTransform;
  colorScale: ScaleLinear<string, string>;
  drag: DragBehavior<Element, any, any>;
  pattern?: CanvasPattern | null;
  projection?: any;
  features?: any;
  originalfeatures?: any;
  borders?: any;
  path?: GeoPath<any, GeoPermissibleObjects>;

  state: State = {
    k: 1,
    isZooming: false,
    tooltipPos: undefined,
  };

  constructor(props: Props, context: any) {
    super(props, context);

    const { width, height, data } = props;

    const counts = Object.values(data).map(val => val.count);

    this.isTouch = isTouchDevice();

    // eslint-disable-next-line consistent-this
    const rThis = this;

    this.simplify = geoTransform({
      point: /* istanbul ignore next */ function point(x, y, z) {
        const { mapTransform } = rThis;
        const scale = mapTransform.scale();
        const area = 1 / scale / scale;
        if (z && z >= area) this.stream.point(x, y);
      },
    });

    const scale0 = (width - 1) / 2 / Math.PI;
    this.mapTransform = geoIdentity()
      .translate([width / 2, height / HEIGHT_TRANSLATE_FACTOR])
      .clipExtent([
        [0, 0],
        [width, height],
      ])
      .scale(scale0);

    this.colorScale = scaleLinear<string, string>()
      .domain([1, max(excludeTopNPercent(counts, PERCENT))!])
      .range([pink, salmon])
      .clamp(true);

    this.drag = d3Drag()
      .filter(/* istanbul ignore next */ () => !this.isTouch)
      .on('drag', this.onDrag);
  }

  componentDidMount() {
    const { canvas, onMapLoadCb } = this;

    this.canvasContext = canvas.current && canvas.current.getContext('2d');
    /* istanbul ignore if */
    if (this.canvasContext) {
      this.canvasContext.lineJoin = 'round';
      this.canvasContext.lineCap = 'round';
    }

    const img = new Image();
    img.src = hatch6;
    this.pattern = ('rgb(231, 231, 231)' as unknown) as CanvasPattern;
    img.onload = /* istanbul ignore next */ () => {
      this.pattern = this.canvasContext && this.canvasContext.createPattern(img, 'repeat');
    };

    getCountries50m().then(onMapLoadCb);
  }

  componentDidUpdate(prevProps: Props) {
    const { width, height } = this.props;
    const { k } = this.state;
    const { colorScale } = this;
    if (!isEqual(this.props.data, prevProps.data)) {
      const counts = Object.values(this.props.data).map(val => val.count);
      colorScale.domain([1, max(excludeTopNPercent(counts, PERCENT))!]);
    }

    if (width !== prevProps.width) {
      const scale0 = (width - 1) / 2 / Math.PI;
      const translate = this.mapTransform.translate();

      this.adjustProjections({
        translate: [
          width / 2 + (translate[0] - prevProps.width / 2),
          height / HEIGHT_TRANSLATE_FACTOR +
            (translate[1] - prevProps.height / HEIGHT_TRANSLATE_FACTOR),
        ],
        clip: [
          [0, 0],
          [this.props.width, this.props.height],
        ],
        scale: scale0 * k,
      });
    }

    if (this.features) {
      this.renderMap();
    }
  }

  UNSAFE_componentWillUnmount() {
    select(this.canvas.current).interrupt('map-zoom');
  }

  onDrag = ({ dx, dy } = d3Event) => {
    const { k } = this.state;
    const { mapTransform, adjustProjections } = this;

    const translate = mapTransform.translate();
    const clip = mapTransform.clipExtent();
    const scale = mapTransform.scale();

    const cX = translate[0] + dx;
    const cY = translate[1] + dy;

    const { x, y } = this.findPosBounds([cX, cY], k);

    adjustProjections({
      translate: [x, y],
      clip: clip!,
      scale,
    });

    this.setState({
      tooltipPos: undefined,
    });
  };

  onZoomButton = (e: React.MouseEvent, buttonType: string) => {
    const { width, height } = this.props;
    const { isZooming, k } = this.state;

    e.preventDefault();

    // disable event while in a zooming transition
    /* istanbul ignore if */
    if (isZooming || !this.features) {
      return;
    }

    const translate = this.mapTransform.translate();

    const scale = k;
    const extent = SCALE_EXTENT;
    let x = translate[0];
    let y = translate[1];
    let signedFactor = buttonType === 'zoom-in' ? ZOOM_FACTOR : 1 / ZOOM_FACTOR;
    let targetScale = scale * signedFactor;
    const center = [width / 2, height / 2];
    const scale0 = (width - 1) / 2 / Math.PI;

    // if we're already at an extent, done
    /* istanbul ignore if */
    if (
      targetScale > Math.max(extent[0], extent[1]) ||
      targetScale < Math.min(extent[0], extent[1])
    ) {
      return;
    }

    // if the signedFactor is too much, scale it down to reach the extent exactly
    const clampedTargetScale = Math.max(extent[0], Math.min(extent[1], targetScale));
    /* istanbul ignore if */
    if (clampedTargetScale !== targetScale) {
      targetScale = clampedTargetScale;
      signedFactor = targetScale / scale;
    }

    // // center each vector, stretch, then put back
    x = (x - center[0]) * signedFactor + center[0];
    y = (y - center[1]) * signedFactor + center[1];

    const pos = this.findPosBounds([x, y], targetScale);

    this.props.onZoomButtonTransition(
      new Promise(resolve => {
        // transition to the new view
        select(this.canvas.current)
          .transition('map-zoom')
          .duration(this.props.zoomDuration)
          .tween('zoom', () => {
            const interpolateScale = interpolate(scale, targetScale);
            const interpolateX = interpolate(translate[0], pos.x);
            const interpolateY = interpolate(translate[1], pos.y);
            return (t: number) => {
              this.adjustProjections({
                translate: [interpolateX(t), interpolateY(t)],
                clip: this.mapTransform.clipExtent()!,
                scale: interpolateScale(t) * scale0,
              });
              this.setState({
                k: interpolateScale(t),
                isZooming: true,
              });
            };
          })
          .on('end', () => {
            this.setState({ isZooming: false });
            resolve();
          });
      }),
    );
  };

  onMouseMove = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    const { data, setHighlightCountry } = this.props;
    const { originalfeatures, projection } = this;
    const point = this.getOffsetPos(e, this.canvas.current as HTMLCanvasElement);
    let highlightCountry: string | undefined;

    if (!originalfeatures) {
      return;
    }

    originalfeatures.features.forEach((country: any) => {
      if (highlightCountry || !Object.hasOwnProperty.call(data, country.properties.iso)) return;
      const coordinates = projection.invert(point);
      /* istanbul ignore if */
      if (geoContains(country, coordinates)) {
        highlightCountry = country.properties.iso;
      }
    });
    setHighlightCountry(highlightCountry);
    this.setState({ tooltipPos: { x: e.clientX, y: e.clientY } });
  };

  onMapLoadCb = (countries: any) => {
    const { width, height } = this.props;
    const { canvas } = this;
    this.originalfeatures = feature(countries, countries.objects.countries);
    this.features = feature(countries, countries.objects.countries);
    this.projection = geoMiller()
      .translate([0, 0])
      .scale(1);

    const geoJson = geoProject(this.features, this.projection);
    const topo = topology({ countries: geoJson });
    const presimp = presimplify(topo as any);
    this.features = feature(presimp, presimp.objects.countries);
    this.borders = mesh(presimp, presimp.objects.countries, (a, b) => a !== b);

    this.adjustProjections({
      translate: [width / 2, height / HEIGHT_TRANSLATE_FACTOR],
      clip: [
        [0, 0],
        [width, height],
      ],
      scale: (width - 1) / 2 / Math.PI,
    });

    this.path = geoPath()
      .projection({
        stream: /* istanbul ignore next */ s => this.simplify.stream(this.mapTransform.stream(s)),
      })
      .context(this.canvasContext!);

    select(canvas.current).call(this.drag as any);

    this.renderMap();
  };

  getOffsetPos = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>, node: HTMLCanvasElement) => {
    // implementation of d3.mouse => https://github.com/d3/d3-selection/blob/master/src/point.js
    const rect = node.getBoundingClientRect();

    return [e.clientX - rect.left - node.clientLeft, e.clientY - rect.top - node.clientTop];
  };

  findPosBounds = (translate: [number, number], scale: number) => {
    const { width, height } = this.props;

    const x =
      Math.min(0, Math.max(translate[0] - (width / 2) * scale, width - width * scale)) +
      (width / 2) * scale;
    const y =
      Math.min(
        0,
        Math.max(
          translate[1] - (height / HEIGHT_TRANSLATE_FACTOR) * scale,
          height - height * scale,
        ),
      ) +
      (height / HEIGHT_TRANSLATE_FACTOR) * scale;

    return {
      x,
      y,
    };
  };

  adjustProjections = (transform: {
    translate: [number, number];
    clip: [[number, number], [number, number]];
    scale: number;
  }) => {
    const { mapTransform, projection, features } = this;

    if (!features) {
      return;
    }

    // I opened an issue on d3-geo about projections not streaming
    // the way transforms stream. Until that is addressed, for dynamic
    // simplification I need to track both a transform and projection.
    // https://github.com/d3/d3-geo/issues/96

    mapTransform
      .translate(transform.translate)
      .clipExtent(transform.clip)
      .scale(transform.scale);

    projection
      .translate(transform.translate)
      .clipExtent(transform.clip)
      .scale(transform.scale);
  };

  renderMap = /* istanbul ignore next */ () => {
    const { data, renderMapCb, width, height, highlightCountry } = this.props;
    const { canvasContext, path, features, borders, colorScale } = this;

    renderMapCb(data);

    if (!canvasContext) return;

    let canvasWidth = width;
    let canvasHeight = height;
    if (window.devicePixelRatio) {
      canvasWidth *= window.devicePixelRatio;
      canvasHeight *= window.devicePixelRatio;
    }

    canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);
    canvasContext.save();

    if (window.devicePixelRatio) {
      canvasContext.scale(window.devicePixelRatio, window.devicePixelRatio);
    }
    // draw countries
    let hoveredCountry: any | undefined;

    features.features.forEach((country: any) => {
      let fill;
      if (data[country.properties.iso] && data[country.properties.iso].count > 0) {
        fill = colorScale(data[country.properties.iso].count);
      } else if (
        (data[country.properties.iso] && data[country.properties.iso].count === 0) ||
        Object.keys(data).length === 0
      ) {
        fill = gray75;
      } else {
        fill = this.pattern;
      }
      canvasContext.beginPath();
      path!(country);
      canvasContext.fillStyle = fill!;
      canvasContext.fill();
      if (highlightCountry === country.properties.iso) {
        hoveredCountry = { feature: country, fill };
      }
    });

    // draw borders
    canvasContext.beginPath();
    canvasContext.lineWidth = 1;
    path!(borders);
    canvasContext.strokeStyle = '#FFF';
    canvasContext.stroke();

    if (hoveredCountry) {
      canvasContext.beginPath();
      path!(hoveredCountry.feature);
      canvasContext.lineWidth = 4;
      canvasContext.strokeStyle = gray95;
      canvasContext.stroke();

      canvasContext.beginPath();
      path!(hoveredCountry.feature);
      canvasContext.lineWidth = 1;
      canvasContext.strokeStyle = gray20;
      canvasContext.stroke();
    }

    canvasContext.restore();
  };

  render() {
    const {
      data,
      width,
      height,
      legendHeight,
      highlightCountry,
      setHighlightCountry,
      stat,
    } = this.props;
    const { k, isZooming, tooltipPos } = this.state;
    const { colorScale, onZoomButton, isTouch } = this;

    let canvasWidth = width;
    let canvasHeight = height;

    /* istanbul ignore if */
    if (window.devicePixelRatio) {
      canvasWidth *= window.devicePixelRatio;
      canvasHeight *= window.devicePixelRatio;
    }

    const isAtMinZoom = k * (1 / ZOOM_FACTOR) < min(SCALE_EXTENT)!;
    const isAtMaxZoom = k * ZOOM_FACTOR >= max(SCALE_EXTENT)!;

    return (
      <div className={classNames(styles.chart, styles[`${BLOCK_NAME}`])} style={{ width }}>
        {!isTouch && (
          <div className={styles[`${BLOCK_NAME}-zoom-controls`]}>
            <button
              type="button"
              data-name="zoom-out-button"
              className={classNames(styles[`${BLOCK_NAME}-zoom-control`], {
                [styles[`${BLOCK_NAME}-disabled`]]: isAtMinZoom && !isZooming,
              })}
              onClick={e => {
                if (!isAtMinZoom) {
                  onZoomButton(e, 'zoom-out');
                }
              }}
              title="Zoom out of map"
            >
              <IconMinusAlt className={`${BLOCK_NAME}-icon`} />
            </button>
            <button
              type="button"
              data-name="zoom-in-button"
              className={classNames(
                styles[`${BLOCK_NAME}-zoom-control`],
                styles[`${BLOCK_NAME}-zoom-in`],
                {
                  [styles[`${BLOCK_NAME}-disabled`]]: isAtMaxZoom && !isZooming,
                },
              )}
              onClick={e => {
                if (!isAtMaxZoom) {
                  onZoomButton(e, 'zoom-in');
                }
              }}
              title="Zoom into map"
            >
              <IconPlusAlt className={styles[`${BLOCK_NAME}-icon`]} />
            </button>
          </div>
        )}
        <div>
          <canvas
            ref={this.canvas}
            width={canvasWidth}
            height={canvasHeight}
            style={{
              width,
              height,
              display: 'block',
            }}
            onMouseMove={this.onMouseMove}
            onMouseLeave={() => {
              setHighlightCountry();
              this.setState({ tooltipPos: undefined });
            }}
          />
          {highlightCountry && tooltipPos && (
            <div
              style={{
                pointerEvents: 'none',
                position: 'fixed',
                top: tooltipPos.y,
                bottom: tooltipPos.y,
                left: tooltipPos.x,
                right: tooltipPos.y,
              }}
            >
              <OverlayTrigger
                placement={OverlayTrigger.top}
                overlay={
                  <span style={{ position: 'relative', top: '-12px' }}>
                    <Tooltip>
                      <div>
                        <Type.h1 variant={Type.cta3} color="gray60" style={{ paddingBottom: '0' }}>
                          {data[highlightCountry].count > 0 &&
                            `#${data[highlightCountry].rank}  ${data[highlightCountry].name}`}
                          {data[highlightCountry].count === 0 && `${data[highlightCountry].name}`}
                        </Type.h1>
                        <span>
                          {data[highlightCountry].formatted_count}{' '}
                          {data[highlightCountry].count === 1 ? stat.slice(0, -1) : stat}
                        </span>
                      </div>
                    </Tooltip>
                  </span>
                }
              />
            </div>
          )}
          <MapChartLegend scale={colorScale} height={legendHeight} />
        </div>
      </div>
    );
  }
}
